import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  SUB_ADMIN: "sub_admin",
};

// Role display labels for UI
export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.SUB_ADMIN]: "Sub Admin",
};

// Available modules that can be assigned to Admin users
export const MODULES = {
  DASHBOARD: "dashboard",
  INQUIRY_MANAGEMENT: "inquiry_management",
  WALLET_MANAGEMENT: "wallet_management",
  MERCHANT_MANAGEMENT: "merchant_management",
  TRANSACTION_MONITORING: "transaction_monitoring",
  FIAT_TRANSACTIONS: "fiat_transactions",
  PAYMENT_MANAGEMENT: "payment_management",
  CONTACT_US: "contact_us",
  CUSTOMER_SUPPORT: "customer_support",
  FAQ_MANAGEMENT: "faq_management",
  CONTENT_MANAGEMENT: "content_management",
  NOTIFICATION: "notification",
  USER_WITHDRAWALS: "user_withdrawals",
};

// Module display names for UI
export const MODULE_LABELS = {
  [MODULES.DASHBOARD]: "Dashboard",
  [MODULES.INQUIRY_MANAGEMENT]: "Inquiry Management",
  [MODULES.WALLET_MANAGEMENT]: "Wallet Management",
  [MODULES.MERCHANT_MANAGEMENT]: "Merchant Management",
  [MODULES.TRANSACTION_MONITORING]: "Transaction Monitoring",
  [MODULES.FIAT_TRANSACTIONS]: "Fiat Transactions",
  [MODULES.PAYMENT_MANAGEMENT]: "Payment Management",
  [MODULES.CONTACT_US]: "Contact Us",
  [MODULES.CUSTOMER_SUPPORT]: "Customer Support",
  [MODULES.FAQ_MANAGEMENT]: "FAQ Management",
  [MODULES.CONTENT_MANAGEMENT]: "Content Management",
  [MODULES.NOTIFICATION]: "Notification",
  [MODULES.USER_WITHDRAWALS]: "User Withdrawals",
};

export const MODULE_ROUTES = {
  [MODULES.DASHBOARD]: "/dashboard",
  [MODULES.INQUIRY_MANAGEMENT]: "/inquiry",
  [MODULES.WALLET_MANAGEMENT]: "/users",
  [MODULES.MERCHANT_MANAGEMENT]: "/merchants",
  [MODULES.TRANSACTION_MONITORING]: "/transaction",
  [MODULES.FIAT_TRANSACTIONS]: "/fiat-transaction",
  [MODULES.PAYMENT_MANAGEMENT]: "/payment-management",
  [MODULES.CONTACT_US]: "/contact-us",
  [MODULES.CUSTOMER_SUPPORT]: "/customer-support",
  [MODULES.FAQ_MANAGEMENT]: "/faq",
  [MODULES.CONTENT_MANAGEMENT]: "/content-management",
  [MODULES.NOTIFICATION]: "/notification-management",
  [MODULES.USER_WITHDRAWALS]: "/user-withdrawals",
};

export const getDefaultRoute = (user) => {
  if (!user) return "/login";
  if (user.role === ROLES.SUPER_ADMIN) return "/dashboard";
  if (user.permissions?.includes(MODULES.DASHBOARD)) return "/dashboard";

  // Find first valid route based on permissions
  const permissions = user.permissions || [];
  for (const permission of permissions) {
    if (MODULE_ROUTES[permission]) {
      return MODULE_ROUTES[permission];
    }
  }

  return "/dashboard"; // Fallback
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Fetch profile data
      const profileResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Also verify token to get role (in case profile doesn't include it)
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/verify-admin-token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const profileData = profileResponse?.data?.data || {};
      const verifyData = verifyResponse?.data?.user || {};

      // Merge profile data with role from verify endpoint
      const userData = {
        ...profileData,
        role: profileData.role || verifyData.role,
      };

      console.log("AuthContext - Final user data:", userData);
      setUser(userData);
      return userData;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Check if user has access to a specific module
  const hasModuleAccess = (module) => {
    if (!user?.role) return false;

    // Super Admin has access to all modules
    if (user.role === ROLES.SUPER_ADMIN) return true;

    // Sub Admin users have access based on their permissions array
    if (user.role === ROLES.SUB_ADMIN) {
      const permissions = user.permissions || [];
      return permissions.includes(module);
    }

    return false;
  };

  const hasRole = (roles) => {
    if (!user?.role) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const isSuperAdmin = () => user?.role === ROLES.SUPER_ADMIN;
  const isSubAdmin = () => user?.role === ROLES.SUB_ADMIN || user?.role === ROLES.SUPER_ADMIN;

  const refreshUser = async () => {
    return await fetchUserProfile();
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        hasModuleAccess,
        hasPermission: hasModuleAccess, // Alias for PrivateRoute compatibility
        hasRole,
        isSuperAdmin,
        isSubAdmin,
        refreshUser,
        logout,
        ROLES,
        MODULES,
        ROLE_LABELS,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
